import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ApprenantDossierBanner from './ApprenantDossierBanner';
import { useRegisterPortalRefresh } from '../../contexts/PortalRefreshContext';
import ApprenantPageShell from './ApprenantPageShell';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import {
  createInscription,
  fetchCohortes,
  fetchDossierStatut,
  fetchFilieres,
  fetchInscription,
  fetchMyInscriptions,
} from '../../services/academiaApprenantService';
import { buildInscriptionPaymentNavigationState, buildCohorteInscriptionIndex } from '../../utils/inscriptionPresentation';
import { getDomaineMetaByKey } from '../../constants/academiaFiliereDomains';
import { formatCohorteCapacityError, isCohorteFull } from '../../utils/cohorteCapacityMessage';

type Filiere = { id: string; code?: string; libelle?: string; domaine?: string };
type CohorteCapacity = {
  capaciteEffective?: number;
  inscrits?: number;
  placesRestantes?: number;
  capaciteSallesMin?: number | null;
};
type Cohorte = {
  id: string;
  code?: string;
  libelle?: string;
  dateDebut?: string;
  dateFin?: string;
  capaciteMax?: number;
  statut?: string;
  capacity?: CohorteCapacity;
};
type DossierStatut = {
  complet: boolean;
  inscriptionEligible?: boolean;
  message?: string;
  messageInscription?: string;
};

function formatShortDate(value?: string) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatSessionRange(dateDebut?: string, dateFin?: string) {
  const start = formatShortDate(dateDebut);
  const end = formatShortDate(dateFin);
  if (start && end) return `${start} → ${end}`;
  return start ?? end ?? null;
}

function filiereDomainIcon(domaine?: string) {
  return getDomaineMetaByKey(domaine)?.icon ?? 'school';
}

function filiereDomainLabel(domaine?: string) {
  const meta = getDomaineMetaByKey(domaine);
  return meta?.title ?? domaine ?? null;
}

export default function ApprenantFormationsPanel() {
  const navigate = useNavigate();
  const [filieres, setFilieres] = useState<Filiere[]>([]);
  const [cohortes, setCohortes] = useState<Cohorte[]>([]);
  const [selectedFiliereId, setSelectedFiliereId] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enrollingId, setEnrollingId] = useState<string | null>(null);
  const [enrolledByCohorteId, setEnrolledByCohorteId] = useState<Map<string, string>>(new Map());
  const [pendingPaymentByCohorteId, setPendingPaymentByCohorteId] = useState<Map<string, string>>(
    new Map(),
  );
  const [dossierStatut, setDossierStatut] = useState<DossierStatut | null>(null);

  const loadCatalogue = async () => {
    setLoadError(false);
    setLoading(true);
    try {
      const [f, ins, dossier] = await Promise.all([
        fetchFilieres(),
        fetchMyInscriptions(),
        fetchDossierStatut().catch(() => null),
      ]);
      setFilieres(f);
      setDossierStatut(dossier);
      const { enrolledByCohorteId: enrolled, pendingPaymentByCohorteId: pending } =
        buildCohorteInscriptionIndex(ins ?? []);
      setEnrolledByCohorteId(enrolled);
      setPendingPaymentByCohorteId(pending);
      if (f.length > 0) setSelectedFiliereId((prev) => prev || f[0].id);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadCatalogue();
  }, []);

  useRegisterPortalRefresh(() => void loadCatalogue());

  useEffect(() => {
    if (!selectedFiliereId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchCohortes(selectedFiliereId);
        if (!cancelled) setCohortes(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Cohortes indisponibles');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedFiliereId]);

  const selectedFiliere = useMemo(
    () => filieres.find((f) => f.id === selectedFiliereId),
    [filieres, selectedFiliereId],
  );

  const handleInscrire = async (idCohorte: string) => {
    setEnrollingId(idCohorte);
    setError(null);
    try {
      let ins = await createInscription(idCohorte);
      if (ins?.id && !ins.lignesFrais?.length) {
        ins = await fetchInscription(ins.id);
      }
      if (!ins?.id) {
        navigate('/app/mes-cours');
        return;
      }

      const paymentNav = buildInscriptionPaymentNavigationState(
        ins,
        selectedFiliere?.libelle ?? selectedFiliere?.code ?? '',
      );

      if (paymentNav) {
        navigate(`/app/inscription/${ins.id}/paiement`, {
          state: paymentNav,
        });
      } else {
        navigate(`/app/inscription/${ins.id}`);
      }
    } catch (e) {
      setError(formatCohorteCapacityError(e, 'Inscription impossible'));
    } finally {
      setEnrollingId(null);
    }
  };

  const openCohortes = cohortes.filter((c) => c.statut !== 'FERMEE' && c.statut !== 'TERMINEE').length;

  return (
    <ApprenantPageShell
      title="Catalogue des formations"
      description="Choisissez une filière et une cohorte. Pièce d'identité validée requise ; les frais (inscription, participation, brevet) sont réglés après souscription."
      sideCard={{
        label: 'Filières disponibles',
        value: loading ? '…' : String(filieres.length),
        helper: selectedFiliere
          ? `${openCohortes || cohortes.length} cohorte${(openCohortes || cohortes.length) > 1 ? 's' : ''} - ${selectedFiliere.libelle || selectedFiliere.code}`
          : dossierStatut?.inscriptionEligible ?? dossierStatut?.complet
            ? 'Pièce d\'identité validée'
            : 'Pièce d\'identité requise avant inscription',
        sideLinks: [
          { label: 'Mon dossier', to: '/app/documents', icon: 'folder' },
          { label: 'Mes cours', to: '/app/mes-cours', icon: 'list_alt' },
        ],
      }}
      onRetry={() => void loadCatalogue()}
      isLoading={loading}
      isError={loadError}
      loadingMessage="Chargement du catalogue…"
      errorMessage="Impossible de charger le catalogue des formations."
    >
      {error ? (
        <p className="whitespace-pre-line rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>
      ) : null}

      <ApprenantDossierBanner dossierStatut={dossierStatut} />

      {!loading && !loadError && (
        <div className="grid gap-6 lg:grid-cols-2">
          <section className="apprenant-formations-panel" aria-labelledby="apprenant-filieres-heading">
            <header className="apprenant-formations-panel__header">
              <span className="apprenant-formations-panel__header-icon" aria-hidden>
                <LucideSymbolIcon name="folder_managed" tone="inherit" size={22} />
              </span>
              <div className="min-w-0">
                <h2 id="apprenant-filieres-heading" className="apprenant-formations-panel__title">
                  Filières
                </h2>
                <p className="apprenant-formations-panel__subtitle">
                  {filieres.length} filière{filieres.length !== 1 ? 's' : ''} · sélectionnez pour voir les cohortes
                </p>
              </div>
            </header>
            <div className="apprenant-formations-panel__body">
              {filieres.length === 0 ? (
                <p className="apprenant-formations-panel__empty">Aucune filière publiée pour le moment.</p>
              ) : (
                <ul className="apprenant-formations-filieres">
                  {filieres.map((f) => {
                    const isActive = selectedFiliereId === f.id;
                    const domainLabel = filiereDomainLabel(f.domaine);
                    return (
                      <li key={f.id}>
                        <button
                          type="button"
                          onClick={() => setSelectedFiliereId(f.id)}
                          className={`apprenant-formations-filiere ${isActive ? 'apprenant-formations-filiere--active' : ''}`}
                          aria-pressed={isActive}
                        >
                          <span className="apprenant-formations-filiere__icon" aria-hidden>
                            <LucideSymbolIcon
                              name={filiereDomainIcon(f.domaine)}
                              tone="inherit"
                              size={20}
                            />
                          </span>
                          <span className="apprenant-formations-filiere__content">
                            <span className="apprenant-formations-filiere__title">
                              {f.libelle || f.code}
                            </span>
                            {f.code ? (
                              <span className="apprenant-formations-filiere__code">{f.code}</span>
                            ) : null}
                            {domainLabel ? (
                              <span className="apprenant-formations-filiere__domain">{domainLabel}</span>
                            ) : null}
                          </span>
                          {isActive ? (
                            <LucideSymbolIcon
                              name="check_circle"
                              tone="inherit"
                              size={18}
                              className="apprenant-formations-filiere__check"
                            />
                          ) : (
                            <LucideSymbolIcon
                              name="arrow_forward"
                              tone="inherit"
                              size={16}
                              className="apprenant-formations-filiere__arrow"
                            />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>

          <section className="apprenant-formations-panel" aria-labelledby="apprenant-cohortes-heading">
            <header className="apprenant-formations-panel__header">
              <span className="apprenant-formations-panel__header-icon" aria-hidden>
                <LucideSymbolIcon name="groups" tone="inherit" size={22} />
              </span>
              <div className="min-w-0">
                <h2 id="apprenant-cohortes-heading" className="apprenant-formations-panel__title">
                  Cohortes ouvertes
                </h2>
                <p className="apprenant-formations-panel__subtitle">
                  {selectedFiliere
                    ? selectedFiliere.libelle || selectedFiliere.code
                    : 'Sélectionnez une filière'}
                  {selectedFiliere && cohortes.length > 0
                    ? ` · ${openCohortes || cohortes.length} session${(openCohortes || cohortes.length) > 1 ? 's' : ''}`
                    : ''}
                </p>
              </div>
            </header>
            <div className="apprenant-formations-panel__body">
              {!selectedFiliereId ? (
                <p className="apprenant-formations-panel__empty">
                  Choisissez une filière à gauche pour afficher les cohortes disponibles.
                </p>
              ) : cohortes.length === 0 ? (
                <p className="apprenant-formations-panel__empty">
                  Aucune cohorte ouverte pour cette filière pour le moment.
                </p>
              ) : (
                <ul className="apprenant-formations-cohortes">
                  {cohortes.map((c) => {
                    const already = enrolledByCohorteId.has(c.id);
                    const pendingPaymentId = pendingPaymentByCohorteId.get(c.id);
                    const pendingPayment = Boolean(pendingPaymentId) && !already;
                    const full = isCohorteFull(c);
                    const places = c.capacity?.placesRestantes;
                    const capacite = c.capacity?.capaciteEffective ?? c.capaciteMax;
                    const inscrits = c.capacity?.inscrits ?? 0;
                    const ratio =
                      capacite != null && capacite > 0
                        ? Math.min(100, Math.round((inscrits / capacite) * 100))
                        : null;
                    const sessionLabel = formatSessionRange(c.dateDebut, c.dateFin);
                    const dossierOk =
                      !dossierStatut ||
                      Boolean(dossierStatut.inscriptionEligible ?? dossierStatut.complet);

                    return (
                      <li
                        key={c.id}
                        className={`apprenant-formations-cohorte ${full ? 'apprenant-formations-cohorte--full' : ''} ${already ? 'apprenant-formations-cohorte--enrolled' : ''} ${pendingPayment ? 'apprenant-formations-cohorte--pending' : ''}`}
                      >
                        <div className="apprenant-formations-cohorte__accent" aria-hidden />
                        <div className="apprenant-formations-cohorte__inner">
                          <header className="apprenant-formations-cohorte__head">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="apprenant-formations-cohorte__title">
                                  {c.libelle || c.code}
                                </h3>
                                {full ? (
                                  <span className="apprenant-formations-cohorte__badge apprenant-formations-cohorte__badge--full">
                                    Complète
                                  </span>
                                ) : already ? (
                                  <span className="apprenant-formations-cohorte__badge apprenant-formations-cohorte__badge--enrolled">
                                    Inscrit
                                  </span>
                                ) : pendingPayment ? (
                                  <span className="apprenant-formations-cohorte__badge apprenant-formations-cohorte__badge--urgent">
                                    Paiement en attente
                                  </span>
                                ) : places != null && places <= 2 ? (
                                  <span className="apprenant-formations-cohorte__badge apprenant-formations-cohorte__badge--urgent">
                                    {places} place{places > 1 ? 's' : ''}
                                  </span>
                                ) : (
                                  <span className="apprenant-formations-cohorte__badge apprenant-formations-cohorte__badge--open">
                                    Ouverte
                                  </span>
                                )}
                              </div>
                              {c.code && c.libelle ? (
                                <p className="apprenant-formations-cohorte__code">{c.code}</p>
                              ) : null}
                            </div>
                          </header>

                          {sessionLabel ? (
                            <p className="apprenant-formations-cohorte__meta">
                              <LucideSymbolIcon name="event" tone="inherit" size={14} />
                              {sessionLabel}
                            </p>
                          ) : null}

                          {capacite != null ? (
                            <div className="apprenant-formations-cohorte__capacity">
                              <div className="flex justify-between text-xs">
                                <span className="font-semibold text-on-surface-variant">Places</span>
                                <span className="font-bold text-slate-900">
                                  {inscrits} / {capacite}
                                </span>
                              </div>
                              {ratio != null ? (
                                <div className="apprenant-formations-cohorte__progress" aria-hidden>
                                  <div
                                    className={`apprenant-formations-cohorte__progress-bar ${full ? 'apprenant-formations-cohorte__progress-bar--full' : ratio >= 80 ? 'apprenant-formations-cohorte__progress-bar--high' : ''}`}
                                    style={{ width: `${ratio}%` }}
                                  />
                                </div>
                              ) : null}
                            </div>
                          ) : null}

                          {full ? (
                            <p className="apprenant-formations-cohorte__hint">
                              Créneau saturé — essayez une autre cohorte ou une autre filière.
                            </p>
                          ) : null}

                          <footer className="apprenant-formations-cohorte__footer">
                            {already ? (
                              <span className="apprenant-formations-cohorte__status">
                                Vous êtes déjà inscrit(e) à cette cohorte
                              </span>
                            ) : pendingPayment && pendingPaymentId ? (
                              <button
                                type="button"
                                onClick={() => navigate(`/app/inscription/${pendingPaymentId}`)}
                                className="apprenant-formations-cohorte__btn apprenant-formations-cohorte__btn--secondary"
                              >
                                <LucideSymbolIcon
                                  name="payments"
                                  tone="inherit"
                                  size={16}
                                  className="text-white"
                                />
                                Finaliser le paiement
                              </button>
                            ) : full ? (
                              <span className="apprenant-formations-cohorte__status apprenant-formations-cohorte__status--muted">
                                Inscriptions closes
                              </span>
                            ) : (
                              <button
                                type="button"
                                disabled={enrollingId === c.id || !dossierOk}
                                onClick={() => void handleInscrire(c.id)}
                                className="apprenant-formations-cohorte__btn"
                              >
                                <LucideSymbolIcon
                                  name="person_add"
                                  tone="inherit"
                                  size={16}
                                  className="text-white"
                                />
                                {enrollingId === c.id ? 'Souscription…' : 'Souscrire'}
                              </button>
                            )}
                          </footer>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>
        </div>
      )}
    </ApprenantPageShell>
  );
}
